import React, { useContext, useEffect, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { Trash, ThumbsUp, MessageCircle } from "lucide-react";
import { useBlogContext } from "../../../../BlogContext.jsx";
import { UserContext } from "../../../../UserContext";
import { fetchBlogById, fetchComments } from "../../../../services/blogService";
import { getBlogId, isBlogAdmin } from "../../../../utils/blogUtils";

const fallbackURL =
  "https://media.istockphoto.com/id/2174551157/photo/cyber-security-data-protection-business-technology-privacy-concept.jpg?s=2048x2048&w=is&k=20&c=sCjAoM5vMvI7PXGFaTaOnRr1AppyOWmxPHkckruBq_A=";

const BlogDetails = () => {
  const { blog_id } = useParams();
  const { deleteBlog } = useBlogContext();
  const { user } = useContext(UserContext);
  const location = useLocation();
  const navigate = useNavigate();

  const [blog, setBlog] = useState(location.state?.blog || null);
  const [likes, setLikes] = useState(location.state?.blog?.like_count || 0);
  const [blogComments, setBlogComments] = useState([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isLoading, setIsLoading] = useState(!location.state?.blog);

  useEffect(() => {
    if (!blog_id) return;
    if (blog && String(getBlogId(blog)) === String(blog_id)) return;

    const loadBlog = async () => {
      setIsLoading(true);
      try {
        const data = await fetchBlogById(blog_id);
        setBlog(data);
        setLikes(data?.like_count || 0);
      } catch (error) {
        console.error("Failed to fetch blog:", error);
        navigate("/blogPage");
      } finally {
        setIsLoading(false);
      }
    };

    loadBlog();
  }, [blog, blog_id, navigate]);

  useEffect(() => {
    if (!blog) return;

    window.scrollTo(0, 0);
    setLikes(blog.like_count || 0);

    const loadComments = async () => {
      try {
        const data = await fetchComments(getBlogId(blog));
        setBlogComments(
          (data ?? []).sort(
            (a, b) => new Date(b.created_at) - new Date(a.created_at)
          )
        );
      } catch (error) {
        console.error("Error fetching comments:", error);
        setBlogComments([]);
      }
    };

    loadComments();
  }, [blog]);

  const confirmDelete = async () => {
    try {
      await deleteBlog(getBlogId(blog));
    } catch (error) {
      console.error("Failed to delete blog:", error);
    } finally {
      setShowDeleteModal(false);
    }
  };

  if (!isBlogAdmin(user)) {
    return <p className="text-center text-red-600 mt-10">Access denied.</p>;
  }

  if (isLoading) {
    return <p className="text-center mt-10">Loading blog...</p>;
  }

  if (!blog) {
    return <p className="text-center text-red-600 mt-10">Blog not found.</p>;
  }

  return (
    <div className="max-w-3xl mx-auto p-6 bg-white shadow rounded-xl mt-8">
      {showDeleteModal && (
        <div className="fixed inset-0 flex items-center justify-center bg-black bg-opacity-40 z-50">
          <div className="bg-white p-6 rounded-lg shadow-md w-full max-w-sm">
            <h2 className="text-lg font-semibold text-gray-800 mb-4">
              Confirm Deletion
            </h2>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete this blog? This action cannot be
              undone.
            </p>
            <div className="flex justify-end gap-2">
              <button
                onClick={() => setShowDeleteModal(false)}
                className="px-4 py-2 bg-gray-200 text-gray-800 rounded"
              >
                Cancel
              </button>
              <button
                onClick={confirmDelete}
                className="px-4 py-2 bg-red-600 text-white rounded"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}

      <img
        src={blog.image_url || fallbackURL}
        alt="Blog"
        className="w-full h-[20%] object-cover"
      />
      <h2 className="text-2xl font-bold mb-2">{blog.title}</h2>

      <hr />
      <div className="flex justify-between text-xs text-gray-500 mt-2">
        <div className="flex items-center mb-2">
          <img
            src={`https://ui-avatars.com/api/?name=${blog.username}`}
            alt={blog.username}
            className="w-7 h-7 rounded-full mr-2"
          />
          <span className="text-sm text-black font-medium">
            {blog.username}
          </span>
        </div>
        <span className="mt-1">
          {new Date(blog.created_at).toLocaleDateString()}
        </span>
      </div>

      <hr />

      {blog.subtitle && (
        <p className="italic text-gray-600 mb-2 mt-2">{blog.subtitle}</p>
      )}

      <div
        className="prose max-w-none text-gray-700"
        dangerouslySetInnerHTML={{ __html: blog.description }}
      />

      {blog.blog_tags && (
        <div className="flex flex-wrap gap-2 mt-4">
          {blog.blog_tags.split(",").map((tag, index) => (
            <span
              key={`${getBlogId(blog)}-${index}`}
              className="bg-blue-100 text-blue-800 text-xs px-2 py-1 rounded-full"
            >
              {tag.trim()}
            </span>
          ))}
        </div>
      )}

      <div className="flex justify-between items-center text-sm text-gray-500 mt-6">
        <span>By: {blog.username}</span>
        <span className="mt-1">
          {new Date(blog.created_at).toLocaleString(undefined, {
            dateStyle: "medium",
            timeStyle: "short",
          })}
        </span>
      </div>

      <div className="mt-6">
        <div className="flex items-center gap-4 text-gray-600">
          <div className="flex items-center gap-1">
            <ThumbsUp className="w-4 h-4" />
            {likes}
          </div>
          <div className="flex items-center gap-1">
            <MessageCircle className="w-4 h-4" />
            {blogComments.length}
          </div>
        </div>

        {blogComments.length > 0 && (
          <div className="mt-6 space-y-4">
            <h4 className="text-md font-semibold">Comments:</h4>
            {blogComments.map((comment) => (
              <div
                key={comment.comment_id}
                className="border border-gray-200 p-3 rounded relative"
              >
                <p className="text-sm text-gray-800 font-medium mb-1">
                  {comment.username}
                </p>
                <div className="flex justify-between items-center">
                  <p className="text-sm text-gray-700">{comment.text}</p>
                  <span className="text-xs text-gray-500 ml-4">
                    {new Date(comment.created_at).toLocaleString(undefined, {
                      dateStyle: "medium",
                      timeStyle: "short",
                    })}
                  </span>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <div className="flex gap-4 mt-6">
        <button
          onClick={() => setShowDeleteModal(true)}
          className="flex items-center gap-1 px-3 py-1 bg-red-100 text-red-800 rounded hover:bg-red-200"
        >
          <Trash size={16} /> Delete
        </button>
      </div>
    </div>
  );
};

export default BlogDetails;
